import { getSupabaseConfig, ensureFreshAccessToken, supabase } from '../../shared/supabase/supabaseClient';
import { reportsQueryScopeToRpcParams } from '../domain/reportsQueryScope';
import type { ReportsQueryScope } from '../domain/reportsQueryScope';
import {
  INTELLIGENCE_SESSION_ERROR,
  type IntelligenceKpisPreset,
  type IntelligenceKpisResponse,
} from '../domain/reportsIntelligenceTypes';
import {
  getReportsCacheIfFresh,
  REPORTS_REMOTE_CACHE_MS,
  reportsCacheKey,
  setReportsCache,
} from './reportsRemoteCache';
import { recordNetworkCall } from '../../shared/dev/networkCallAudit';

export type {
  IntelligenceKpisPreset,
  RentabilidadDailyRow,
  TurnoKpiHistoryRow,
  IntelligenceKpisResponse,
} from '../domain/reportsIntelligenceTypes';

export { INTELLIGENCE_SESSION_ERROR } from '../domain/reportsIntelligenceTypes';

type IntelligenceKpisParams = {
  branchId: string;
  preset?: Exclude<IntelligenceKpisPreset, 'custom'>;
  fromIso?: string;
  toIso?: string;
  limitTurnos?: number;
  queryScope?: ReportsQueryScope | null;
};

function intelligenceKpisCacheKey(params: IntelligenceKpisParams): string {
  const scopeParams = reportsQueryScopeToRpcParams(params.queryScope);
  return reportsCacheKey([
    'intelligence_kpis',
    params.branchId,
    params.preset ?? '',
    params.fromIso ?? '',
    params.toIso ?? '',
    params.limitTurnos ?? '',
    scopeParams.p_product_id ?? '',
    scopeParams.p_category ?? '',
    scopeParams.p_customer_id ?? '',
    scopeParams.p_cashier_user_id ?? '',
  ]);
}

/**
 * POST explícito con Bearer del usuario (evita depender solo del fetch interno del cliente).
 * Reintenta una vez tras refreshSession si el gateway devuelve 401.
 */
async function fetchIntelligenceKpisRemote(params: IntelligenceKpisParams): Promise<IntelligenceKpisResponse> {
  const { url, anonKey } = getSupabaseConfig();
  const { queryScope, ...rest } = params;
  const requestBody = {
    ...rest,
    ...reportsQueryScopeToRpcParams(queryScope),
  };

  const request = async (after401Refresh: boolean): Promise<IntelligenceKpisResponse> => {
    if (!after401Refresh) {
      const token = await ensureFreshAccessToken();
      if (!token) {
        throw new Error(INTELLIGENCE_SESSION_ERROR);
      }
    } else {
      const { error } = await supabase.auth.refreshSession();
      if (error) {
        throw new Error(INTELLIGENCE_SESSION_ERROR);
      }
    }

    const accessToken = (await supabase.auth.getSession()).data.session?.access_token;
    if (!accessToken) {
      throw new Error(INTELLIGENCE_SESSION_ERROR);
    }

    const res = await fetch(`${url}/functions/v1/get-intelligence-kpis`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: anonKey,
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(requestBody),
    });

    const json = (await res.json().catch(() => ({}))) as Record<string, unknown>;

    if (res.status === 401 && !after401Refresh) {
      return request(true);
    }

    if (!res.ok) {
      if (res.status === 401) {
        throw new Error(INTELLIGENCE_SESSION_ERROR);
      }
      const msg =
        typeof json.error === 'string'
          ? json.error
          : typeof json.message === 'string'
            ? json.message
            : `No se pudieron cargar los datos (${res.status}).`;
      throw new Error(msg);
    }

    const body = json as Partial<IntelligenceKpisResponse> & { error?: string; ok?: boolean };
    if (body?.error) throw new Error(body.error);
    if (!body?.ok) throw new Error(body?.error ?? 'Respuesta inválida del servidor');
    return body as IntelligenceKpisResponse;
  };

  return request(false);
}

export const getIntelligenceKpis = async (params: IntelligenceKpisParams): Promise<IntelligenceKpisResponse> => {
  const cacheKey = intelligenceKpisCacheKey(params);
  const cached = getReportsCacheIfFresh<IntelligenceKpisResponse>(cacheKey, REPORTS_REMOTE_CACHE_MS);
  if (cached) {
    recordNetworkCall({ category: 'reports', name: 'get-intelligence-kpis', cached: true });
    return cached;
  }

  recordNetworkCall({ category: 'reports', name: 'get-intelligence-kpis' });
  const fresh = await fetchIntelligenceKpisRemote(params);
  setReportsCache(cacheKey, fresh);
  return fresh;
};
